// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { AppIcon } from '@shared/ui-kit/components';
import { Label, type LabelProps } from '@shared/ui-kit/components/input';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { classNames } from '@shared/ui-kit/utils/classnames';
import { IconButton, Tooltip } from '@mui/material';
import type { ComponentProps, FC, ReactNode } from 'react';

export type LabelWithInfoTooltipProps = LabelProps & {
  tooltipI18nKey: ComponentProps<typeof Translation>['i18nKey'];
  tooltipNs?: ComponentProps<typeof Translation>['ns'];
  children: ReactNode;
};

export const LabelWithInfoTooltip: FC<LabelWithInfoTooltipProps> = ({
  tooltipI18nKey,
  tooltipNs = 'aggregator',
  children,
  className,
  ...labelProps
}) => (
  <Label
    {...labelProps}
    className={classNames('!inline-flex !items-center !gap-1', className)}
  >
    {children}
    <Tooltip
      title={<Translation i18nKey={tooltipI18nKey} ns={tooltipNs} />}
      placement="top"
    >
      <IconButton
        type="button"
        size="small"
        tabIndex={-1}
        aria-label="info"
        className="!ml-0.5 !p-0.5"
        onClick={event => event.preventDefault()}
      >
        <AppIcon icon="Info" size="sm" color="disabled" aria-hidden />
      </IconButton>
    </Tooltip>
  </Label>
);

LabelWithInfoTooltip.displayName = 'LabelWithInfoTooltip';
